/**
 * Shared helpers for the global learner-search "find in page" highlight.
 *
 * `renderHighlightedText` wraps every case-insensitive occurrence of `term` inside
 * `text` with a styled `<mark className="search-result-mark">` (CSS lives in
 * `index.css`). NFC-normalizes both sides so the same Persian visual word matches
 * regardless of canonical / compatibility form mismatches.
 */

import React from 'react';

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Returns `text` untouched when either side is empty. Safe to call unconditionally. */
export function renderHighlightedText(
  text: string | undefined | null,
  term: string | undefined | null
): React.ReactNode {
  if (typeof text !== 'string' || text.length === 0) return text ?? null;
  const t = term?.trim();
  if (!t) return text;
  const normalizedTerm = t.normalize('NFC');
  const normalizedText = text.normalize('NFC');
  const parts = normalizedText.split(new RegExp(`(${escapeRegex(normalizedTerm)})`, 'gi'));
  const lowered = normalizedTerm.toLowerCase();
  return parts.map((part, i) =>
    part.toLowerCase() === lowered ? (
      <mark key={i} className="search-result-mark">
        {part}
      </mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

/**
 * HTML-string version of `renderHighlightedText` for content that has to be fed
 * to `dangerouslySetInnerHTML` (rich-text bodies, tip-formatted content, etc.).
 *
 * Walks every text node inside a parsed copy of `html`, splits each occurrence
 * of `term` and wraps the matches with `<mark class="search-result-mark">`.
 * Text inside `<script>`, `<style>`, existing marks, or attribute values is not
 * touched, so the resulting HTML stays well-formed and safe to re-render.
 *
 * NFC-normalises both sides for Farsi parity. Returns `html` unchanged when
 * either side is empty so callers can use it unconditionally.
 */
export function injectHighlightMarks(html: string, term: string | undefined | null): string {
  if (typeof html !== 'string' || html.length === 0) return typeof html === 'string' ? html : '';
  const t = term?.trim();
  if (!t) return html;
  if (typeof document === 'undefined') return html;

  const normalizedTerm = t.normalize('NFC');
  const lowered = normalizedTerm.toLowerCase();
  const termLen = normalizedTerm.length;

  const container = document.createElement('div');
  container.innerHTML = html;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = (node as Text).parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      const tag = parent.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE') return NodeFilter.FILTER_REJECT;
      if (parent.closest('mark.search-result-mark')) return NodeFilter.FILTER_REJECT;
      const text = (node.textContent || '').normalize('NFC');
      return text.toLowerCase().includes(lowered) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    }
  });

  // Snapshot first – mutating during walk would invalidate the iterator.
  const matches: Text[] = [];
  while (walker.nextNode()) {
    matches.push(walker.currentNode as Text);
  }

  matches.forEach((node) => {
    const original = (node.textContent || '').normalize('NFC');
    const lowerOriginal = original.toLowerCase();
    const fragment = document.createDocumentFragment();
    let cursor = 0;
    let idx = lowerOriginal.indexOf(lowered);
    while (idx !== -1) {
      if (idx > cursor) {
        fragment.appendChild(document.createTextNode(original.slice(cursor, idx)));
      }
      const mark = document.createElement('mark');
      mark.className = 'search-result-mark';
      mark.textContent = original.slice(idx, idx + termLen);
      fragment.appendChild(mark);
      cursor = idx + termLen;
      idx = lowerOriginal.indexOf(lowered, cursor);
    }
    if (cursor < original.length) {
      fragment.appendChild(document.createTextNode(original.slice(cursor)));
    }
    node.parentNode?.replaceChild(fragment, node);
  });

  return container.innerHTML;
}

/**
 * Case-/NFC-insensitive substring check that treats `html` as plain text
 * (HTML tags are stripped before the compare). Useful for deciding whether a
 * particular question / option / block "matched" the global search term so the
 * caller can flash its container with `.search-result-highlight`.
 */
export function htmlContainsTerm(html: string | undefined | null, term: string | undefined | null): boolean {
  if (!html) return false;
  const t = term?.trim();
  if (!t) return false;
  const lowered = t.normalize('NFC').toLowerCase();
  let plain = html;
  if (typeof document !== 'undefined') {
    const div = document.createElement('div');
    div.innerHTML = html;
    plain = div.textContent || '';
  } else {
    plain = html.replace(/<[^>]+>/g, ' ');
  }
  return plain.normalize('NFC').toLowerCase().includes(lowered);
}
